import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import { useTalents } from './useTalents'

const STORAGE_KEY = 'cs-talent'

interface SelectedTalentContextValue {
  selectedTalentId: string
  setSelectedTalentId: (id: string) => void
}

const SelectedTalentContext = createContext<SelectedTalentContextValue | null>(null)

export function SelectedTalentProvider({ children }: { children: ReactNode }) {
  const { talents, loading } = useTalents()

  const [selectedTalentId, setSelectedTalentId] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY) ?? 't1',
  )

  // On first successful load, if the stored id is missing or stale,
  // default to the first talent in the list.
  useEffect(() => {
    if (loading || talents.length === 0) return
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored || !talents.some((t) => t.id === stored)) {
      const first = talents[0].id
      setSelectedTalentId(first)
      localStorage.setItem(STORAGE_KEY, first)
    }
  }, [loading, talents])

  const setSelectedTalent = useCallback((id: string) => {
    setSelectedTalentId(id)
    localStorage.setItem(STORAGE_KEY, id)
  }, [])

  return (
    <SelectedTalentContext.Provider
      value={{ selectedTalentId, setSelectedTalentId: setSelectedTalent }}
    >
      {children}
    </SelectedTalentContext.Provider>
  )
}

export function useSelectedTalent(): SelectedTalentContextValue {
  const ctx = useContext(SelectedTalentContext)
  if (!ctx) {
    throw new Error('useSelectedTalent must be used within SelectedTalentProvider')
  }
  return ctx
}
